import React, { useCallback, useMemo } from "react";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import {
  useFormikContext,
  FieldArray,
  FormikErrors,
  FormikTouched,
} from "formik";
import { ExpenseFormData, ExpenseEntry, PaymentDetail } from "./types";
import Paper from "@mui/material/Paper";
import {
  formatCurrency,
  formatDate,
  formatInputCurrency,
} from "../../../utils/utils";
import { smallButtonProp } from "../../../utils/styles";
import { TripData } from "../../trip/dailyBusTrip/types";
import { useState } from "react";

const PAYMENT_MODES = [
  "CASH",
  "BANK_TRANSFER",
  "UPI",
  "CHEQUE",
  "ONLINE",
  "OTHER",
  "FUEL_CARD",
] as const;

const TRIP_PAYMENT_MODES = [
  "CASH",
  "ADVANCE_CASH",
  "BANK_TRANSFER",
  "UPI",
  "CHEQUE",
  "ONLINE",
  "OTHER",
] as const;

export const TextFormatter = {
  CASH: "Cash",
  ADVANCE_CASH: "Advance Cash",
  BANK_TRANSFER: "Bank Transfer",
  UPI: "UPI",
  CHEQUE: "Cheque",
  ONLINE: "Online",
  OTHER: "Other",
  FINAL: "Final",
  ADVANCE: "Advance",
};

interface PaymentDetailsFieldsProps {
  index: number;
  isTripExpense?: boolean;
  tripData?: TripData;
}

const PaymentDetailsFields: React.FC<PaymentDetailsFieldsProps> = ({
  index,
  isTripExpense = false,
  tripData,
}) => {
  const { values, errors, touched, setFieldValue, validateField } =
    useFormikContext<ExpenseFormData>();
  const expense = values.expenses[index];
  const payments = expense.payments || [];
  const includesFuelCardPayment = payments.some(
    (p) => p.mode === "FUEL_CARD" && !p.isDeleted
  );
  const expenseErrors = errors.expenses?.[index] as
    | FormikErrors<ExpenseEntry>
    | undefined;
  const expenseTouched = touched.expenses?.[index] as
    | FormikTouched<ExpenseEntry>
    | undefined;
  const paymentErrors = expenseErrors?.payments as
    | FormikErrors<PaymentDetail>[]
    | undefined;
  const paymentTouched = expenseTouched?.payments as
    | FormikTouched<PaymentDetail>[]
    | undefined;
  const [addRemarks, setAddRemarks] = useState(false);

  // Calculate total of non-deleted payments using useMemo
  const totalPaidAmount = useMemo(
    () =>
      payments
        .filter((p) => !p.isDeleted)
        .reduce((sum, p) => sum + (Number(p.amount) || 0), 0),
    [payments] // Recalculate only when payments array changes
  );

  // Calculate remaining amount using useMemo
  const remainingAmount = useMemo(
    () => Number(expense.amount) - totalPaidAmount,
    [expense.amount, totalPaidAmount] // Recalculate when expense amount or total paid amount changes
  );

  const handlePaymentChange = (
    paymentIndex: number,
    field: string,
    value: string | boolean
  ) => {
    // Only set isEdited on the payment itself, not the expense
    setFieldValue(`expenses.${index}.payments.${paymentIndex}.isEdited`, true);
    setFieldValue(`expenses.${index}.payments.${paymentIndex}.${field}`, value);
  };

  // Format ISO date to YYYY-MM-DD
  const formatDateForInput = (isoDate: string) => {
    if (!isoDate) return "";
    return isoDate.split("T")[0];
  };

  // Calculate total advance used from all payments
  const calculateTotalAdvanceUsed = useCallback(
    (
      expensesList: ExpenseEntry[],
      excludeExpenseIndex?: number,
      excludePaymentIndex?: number
    ) => {
      if (!expensesList) return 0;
      return expensesList.reduce((total, expense, expenseIndex) => {
        const advancePayments =
          expense.payments?.filter((payment, paymentIndex) => {
            // Exclude the current payment being edited from calculation
            if (
              expenseIndex === excludeExpenseIndex &&
              paymentIndex === excludePaymentIndex
            ) {
              return false;
            }
            return payment?.mode === "ADVANCE_CASH" && !payment.isDeleted;
          }) || [];

        const expenseAdvanceTotal = advancePayments.reduce(
          (sum, payment) => sum + Number(payment.amount || 0),
          0
        );

        return total + expenseAdvanceTotal;
      }, 0);
    },
    []
  );

  const getRemainingAdvanceCash = useCallback(
    (
      values: ExpenseFormData,
      expensesList: ExpenseEntry[],
      excludeExpenseIndex?: number,
      excludePaymentIndex?: number
    ) => {
      const totalUsed = calculateTotalAdvanceUsed(
        expensesList,
        excludeExpenseIndex,
        excludePaymentIndex
      );
      const previousAdvanceBalance =
        Number(tripData?.previousTrip?.advanceBalance || 0) -
        Number(tripData?.previousTrip?.balanceRecoveredAmount || 0);
      const remaining =
        Number(values.advancePayment) +
        Number(previousAdvanceBalance) -
        Number(totalUsed);

      return remaining;
    },
    [calculateTotalAdvanceUsed, tripData?.previousTrip?.advanceBalance, tripData?.previousTrip?.balanceRecoveredAmount]
  );

  // Helper function to determine if ADVANCE_CASH should be available for a specific payment
  const shouldShowAdvanceCashOption = useCallback(
    (paymentIndex: number, currentPayment: PaymentDetail) => {
      // Always show ADVANCE_CASH if the current payment is already set to ADVANCE_CASH
      if (currentPayment.mode === "ADVANCE_CASH") {
        return true;
      }

      // For new payments, check if there's remaining advance cash (excluding current payment from calculation)
      const remainingAdvance = getRemainingAdvanceCash(
        values,
        values.expenses,
        index,
        paymentIndex
      );
      return remainingAdvance > 0;
    },
    [getRemainingAdvanceCash, values, index]
  );

  const newPayment: PaymentDetail = {
    type: "ADVANCE",
    mode: "CASH",
    amount: 0,
    date: new Date().toISOString().split("T")[0],
    location: expense?.location || "",
    receivedFrom: expense?.paidTo || "",
    remarks: "",
    status: "PENDING",
    isNew: true,
    rentalJourneyExpenseId: expense?.id || null,
    masterExpenseId: expense?.id || null,
  };

  return (
    <FieldArray name={`expenses.${index}.payments`}>
      {({ push, remove }) => (
        <>
          {payments.length === 0 && !includesFuelCardPayment && (
            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-start",
                  alignItems: "center",
                  borderRadius: 1,
                }}
              >
                <Button
                  startIcon={
                    <AddIcon
                      style={{ fontSize: smallButtonProp.sx.fontSize }}
                    />
                  }
                  onClick={() => push(newPayment)}
                  variant="outlined"
                  {...smallButtonProp}
                >
                  Add Payment
                </Button>
              </Box>
            </Grid>
          )}

          {!includesFuelCardPayment &&
            payments.map((payment, paymentIndex) => (
              <React.Fragment key={paymentIndex}>
                {!payment.isDeleted ? (
                  <Grid container item xs={11} spacing={1}>
                    <Grid item xs={11}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 0.5 }}
                      >
                        <Typography
                          variant="caption"
                          sx={{ color: "text.secondary" }}
                        >
                          Payment
                        </Typography>
                        <IconButton
                          color="error"
                          onClick={() => {
                            if (payment.id) {
                              handlePaymentChange(
                                paymentIndex,
                                "isDeleted",
                                true
                              );
                            } else {
                              remove(paymentIndex);
                            }
                          }}
                          size="small"
                          sx={{ p: 0.25, ml: "auto" }}
                        >
                          <DeleteIcon sx={{ fontSize: "0.875rem" }} />
                        </IconButton>
                      </Box>
                    </Grid>

                    <Grid item xs={11} sm={2}>
                      <TextField
                        required
                        fullWidth
                        size="small"
                        type="date"
                        label="Date"
                        name={`expenses.${index}.payments.${paymentIndex}.date`}
                        value={formatDateForInput(payment.date) || ""}
                        onChange={(e) =>
                          handlePaymentChange(
                            paymentIndex,
                            "date",
                            e.target.value
                          )
                        }
                        onBlur={() => {
                          validateField(
                            `expenses.${index}.payments.${paymentIndex}.date`
                          );
                        }}
                        error={Boolean(
                          paymentTouched?.[paymentIndex]?.date &&
                            paymentErrors?.[paymentIndex]?.date
                        )}
                        helperText={
                          paymentTouched?.[paymentIndex]?.date &&
                          paymentErrors?.[paymentIndex]?.date
                        }
                        InputLabelProps={{ shrink: true }}
                        sx={{
                          "& .MuiInputBase-root": { fontSize: "0.75rem" },
                          "& .MuiInputLabel-root": { fontSize: "0.75rem" },
                          "& .MuiFormHelperText-root": { fontSize: "0.65rem" },
                        }}
                      />
                    </Grid>

                    {/* <Grid item xs={12} sm={3}>
                      <TextField
                        required
                        fullWidth
                        select
                        size="small"
                        label="Type"
                        name={`expenses.${index}.payments.${paymentIndex}.type`}
                        value={payment.type || ""}
                        onChange={(e) =>
                          handlePaymentChange(
                            paymentIndex,
                            "type",
                            e.target.value
                          )
                        }
                        onBlur={() => {
                          validateField(
                            `expenses.${index}.payments.${paymentIndex}.type`
                          );
                        }}
                        error={Boolean(
                          paymentTouched?.[paymentIndex]?.type &&
                            paymentErrors?.[paymentIndex]?.type
                        )}
                        helperText={
                          paymentTouched?.[paymentIndex]?.type &&
                          paymentErrors?.[paymentIndex]?.type
                        }
                        sx={{
                          "& .MuiInputBase-root": { fontSize: "0.75rem" },
                          "& .MuiInputLabel-root": { fontSize: "0.75rem" },
                          "& .MuiFormHelperText-root": { fontSize: "0.65rem" },
                        }}
                      >
                        {PAYMENT_TYPES.map((type) => (
                          <MenuItem
                            sx={{ fontSize: "0.75rem" }}
                            key={type}
                            value={type}
                          >
                            {TextFormatter[type as keyof typeof TextFormatter]}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid> */}

                    <Grid item xs={12} sm={2}>
                      <TextField
                        required
                        fullWidth
                        select
                        size="small"
                        label="Mode"
                        name={`expenses.${index}.payments.${paymentIndex}.mode`}
                        value={payment.mode || ""}
                        onChange={(e) =>
                          handlePaymentChange(
                            paymentIndex,
                            "mode",
                            e.target.value
                          )
                        }
                        onBlur={() => {
                          validateField(
                            `expenses.${index}.payments.${paymentIndex}.mode`
                          );
                        }}
                        error={Boolean(
                          paymentTouched?.[paymentIndex]?.mode &&
                            paymentErrors?.[paymentIndex]?.mode
                        )}
                        helperText={
                          paymentTouched?.[paymentIndex]?.mode &&
                          paymentErrors?.[paymentIndex]?.mode
                        }
                        sx={{
                          "& .MuiInputBase-root": { fontSize: "0.75rem" },
                          "& .MuiInputLabel-root": { fontSize: "0.75rem" },
                          "& .MuiFormHelperText-root": { fontSize: "0.65rem" },
                        }}
                      >
                        {isTripExpense
                          ? TRIP_PAYMENT_MODES.map((mode) => (
                              <MenuItem
                                sx={{ fontSize: "0.75rem" }}
                                key={mode}
                                value={mode}
                              >
                                {
                                  TextFormatter[
                                    mode as keyof typeof TextFormatter
                                  ]
                                }
                              </MenuItem>
                            ))
                          : PAYMENT_MODES.map((mode) => (
                              <MenuItem
                                sx={{ fontSize: "0.75rem" }}
                                key={mode}
                                value={mode}
                              >
                                {
                                  TextFormatter[
                                    mode as keyof typeof TextFormatter
                                  ]
                                }
                              </MenuItem>
                            ))}
                      </TextField>
                    </Grid>

                    <Grid item xs={12} sm={2}>
                      <TextField
                        required
                        fullWidth
                        size="small"
                        type="text"
                        label="Amount"
                        name={`expenses.${index}.payments.${paymentIndex}.amount`}
                        value={
                          payment.amount
                            ? formatInputCurrency(payment.amount)
                            : ""
                        }
                        onChange={(e) => {
                          const value = e.target.value.replace(/,/g, "");
                          if (value === "" || /^\d*\.?\d*$/.test(value)) {
                            handlePaymentChange(paymentIndex, "amount", value);
                          }
                        }}
                        onBlur={() => {
                          validateField(
                            `expenses.${index}.payments.${paymentIndex}.amount`
                          );
                        }}
                        error={Boolean(
                            paymentErrors?.[paymentIndex]?.amount
                        )}
                        helperText={
                          paymentErrors?.[paymentIndex]?.amount
                        }
                        InputProps={{
                          startAdornment: (
                            <Typography
                              component="span"
                              sx={{ mr: 0.5, fontSize: "0.75rem" }}
                            >
                              ₹
                            </Typography>
                          ),
                          inputProps: {
                            inputMode: "decimal",
                          },
                        }}
                        sx={{
                          "& .MuiInputBase-root": { fontSize: "0.75rem" },
                          "& .MuiInputLabel-root": { fontSize: "0.75rem" },
                          "& .MuiFormHelperText-root": { fontSize: "0.65rem" },
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={2}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Location"
                        name={`expenses.${index}.payments.${paymentIndex}.location`}
                        value={payment.location}
                        onChange={(e) =>
                          handlePaymentChange(
                            paymentIndex,
                            "location",
                            e.target.value
                          )
                        }
                        onBlur={() => {
                          validateField(
                            `expenses.${index}.payments.${paymentIndex}.location`
                          );
                        }}
                        error={Boolean(
                          paymentTouched?.[paymentIndex]?.location &&
                            paymentErrors?.[paymentIndex]?.location
                        )}
                        helperText={
                          paymentTouched?.[paymentIndex]?.location &&
                          paymentErrors?.[paymentIndex]?.location
                        }
                        sx={{
                          "& .MuiInputBase-root": { fontSize: "0.75rem" },
                          "& .MuiInputLabel-root": { fontSize: "0.75rem" },
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={2}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Paid To"
                        name={`expenses.${index}.payments.${paymentIndex}.receivedFrom`}
                        value={payment.receivedFrom}
                        onChange={(e) =>
                          handlePaymentChange(
                            paymentIndex,
                            "receivedFrom",
                            e.target.value
                          )
                        }
                        error={Boolean(
                          paymentTouched?.[paymentIndex]?.receivedFrom &&
                            paymentErrors?.[paymentIndex]?.receivedFrom
                        )}
                        helperText={
                          paymentTouched?.[paymentIndex]?.receivedFrom &&
                          paymentErrors?.[paymentIndex]?.receivedFrom
                        }
                        sx={{
                          "& .MuiInputBase-root": { fontSize: "0.75rem" },
                          "& .MuiInputLabel-root": { fontSize: "0.75rem" },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        variant="outlined"
                        onClick={() => setAddRemarks(!addRemarks)}
                        size={smallButtonProp.size}
                        sx={{
                          ...smallButtonProp.sx,
                          color: "grey",
                          borderColor: "grey.400",
                          "&:hover": {
                            borderColor: "grey.400",
                            color: "grey",
                          },
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ fontSize: "0.65rem" }}
                        >
                          {addRemarks ? "Hide Remarks" : "Add Remarks"}
                        </Typography>
                      </Button>
                    </Grid>
                    {addRemarks && (
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Remarks"
                          name={`expenses.${index}.payments.${paymentIndex}.remarks`}
                          value={payment.remarks}
                          onChange={(e) =>
                            handlePaymentChange(
                              paymentIndex,
                              "remarks",
                              e.target.value
                            )
                          }
                          multiline
                          onBlur={() => {
                            validateField(
                              `expenses.${index}.payments.${paymentIndex}.remarks`
                            );
                          }}
                          error={Boolean(
                            paymentTouched?.[paymentIndex]?.remarks &&
                              paymentErrors?.[paymentIndex]?.remarks
                          )}
                          helperText={
                            paymentTouched?.[paymentIndex]?.remarks &&
                            paymentErrors?.[paymentIndex]?.remarks
                          }
                          sx={{
                            "& .MuiInputBase-root": { fontSize: "0.75rem" },
                            "& .MuiInputLabel-root": { fontSize: "0.75rem" },
                          }}
                        />
                      </Grid>
                    )}
                  </Grid>
                ) : (
                  payment.mode !== "FUEL_CARD" && (
                    <Grid item xs={9}>
                      <Paper
                        sx={{
                          p: 1,
                          backgroundColor: "#f5f5f5",
                          border: "1px solid #e0e0e0",
                          position: "relative",
                        }}
                      >
                        <Button
                          size="small"
                          sx={{
                            position: "absolute",
                            top: 4,
                            right: 4,
                            p: 0.5,
                            fontSize: "0.75rem",
                            fontStyle: "italic",
                          }}
                          onClick={() => {
                            handlePaymentChange(
                              paymentIndex,
                              "isDeleted",
                              false
                            );
                          }}
                        >
                          Undo Delete
                        </Button>

                        <Grid container spacing={1}>
                          <Grid item xs={1}>
                            <Typography
                              variant="caption"
                              color="textSecondary"
                              display="block"
                            >
                              Type
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ fontSize: "0.75rem" }}
                            >
                              {payment.type}
                            </Typography>
                          </Grid>
                          <Grid item xs={1}>
                            <Typography
                              variant="caption"
                              color="textSecondary"
                              display="block"
                            >
                              Mode
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ fontSize: "0.75rem" }}
                            >
                              {payment.mode}
                            </Typography>
                          </Grid>
                          <Grid item xs={1}>
                            <Typography
                              variant="caption"
                              color="textSecondary"
                              display="block"
                            >
                              Amount
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ fontSize: "0.75rem" }}
                            >
                              ₹{payment.amount}
                            </Typography>
                          </Grid>
                          <Grid item xs={1}>
                            <Typography
                              variant="caption"
                              color="textSecondary"
                              display="block"
                            >
                              Date
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ fontSize: "0.75rem" }}
                            >
                              {formatDate(payment.date)}
                            </Typography>
                          </Grid>
                          <Grid item xs={2}>
                            <Typography
                              variant="caption"
                              color="textSecondary"
                              display="block"
                            >
                              Location
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ fontSize: "0.75rem" }}
                            >
                              {payment.location}
                            </Typography>
                          </Grid>
                          <Grid item xs={2}>
                            <Typography
                              variant="caption"
                              color="textSecondary"
                              display="block"
                            >
                              Paid To
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ fontSize: "0.75rem" }}
                            >
                              {payment.receivedFrom}
                            </Typography>
                          </Grid>
                          <Grid item xs={2}>
                            <Typography
                              variant="caption"
                              color="textSecondary"
                              display="block"
                            >
                              Remark
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ fontSize: "0.75rem" }}
                            >
                              {payment.remarks}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Paper>
                    </Grid>
                  )
                )}
              </React.Fragment>
            ))}
          <Grid item xs={10}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1,
                border: "1px dashed grey",
                p: 0.5,
                borderRadius: 1,
              }}
            >
              <Typography variant="subtitle2" sx={{ fontSize: "0.75rem" }}>
                Payment Details
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Typography
                  variant="caption"
                  color={remainingAmount < 0 ? "error" : "textSecondary"}
                >
                  Balance: {formatCurrency(remainingAmount)}
                  {remainingAmount < 0 && " (Exceeds expense amount)"}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Total Paid: {formatCurrency(totalPaidAmount)}
                </Typography>
              </Box>
            </Box>
          </Grid>

          {/* {payments.length > 0 &&
            !includesFuelCardPayment &&
            !isTripExpense && (
              <Grid item xs={12}>
                <Button
                  startIcon={<AddIcon sx={{ fontSize: "0.875rem" }} />}
                  onClick={() => push(newPayment)}
                  sx={{
                    my: 0.5,
                    fontSize: "0.75rem",
                    textTransform: "none",
                    color: "primary.main",
                    minWidth: "auto",
                    "&:hover": {
                      backgroundColor: "transparent",
                      textDecoration: "underline",
                    },
                  }}
                >
                  Add another payment
                </Button>
              </Grid>
            )} */}
        </>
      )}
    </FieldArray>
  );
};

export default PaymentDetailsFields;
